/*
 * card-quick-add-overlay.js — in-card size selection + add to cart.
 *
 * Replaces the old cart-icon -> <quick-add-modal> path on product cards. The
 * plus trigger reveals an overlay drawn over the card image carrying the
 * product's option buttons (Waist / Length, or Size) and an ADD TO CART button.
 *
 * ARCHITECTURE NOTES — read before changing:
 *
 * 1. FULLY DELEGATED, ZERO PER-ELEMENT INIT. Collection grids (facets,
 *    quick-filters), the homepage switcher sections and card-color-swap all
 *    replace card markup wholesale via innerHTML, and <script> tags inside
 *    injected HTML never execute. Every listener here is on `document`, and all
 *    per-overlay state is derived lazily from the DOM, so swapped-in cards work
 *    with no re-init. Do not add a DOMContentLoaded/section:load bootstrap.
 *
 * 2. THE VARIANT MATRIX IS SERVER-RENDERED. Each overlay carries a
 *    <script type="application/json" data-card-quick-add-data> block written by
 *    snippets/card-quick-add-overlay.liquid. application/json is inert, so it
 *    survives innerHTML injection intact. Opening an overlay costs no network.
 *
 * 3. ADD TO CART GOES THROUGH <product-form>. That gives us the cart drawer
 *    refresh, sold-out handling, error messaging and loading states for free,
 *    identical to the PDP. We only write the chosen variant id into the form's
 *    hidden [name=id] input and enable/disable its submit button.
 *
 * Availability is CASCADING: option 1 (Waist) shows every value that has any
 * available variant; option 2 (Length) is filtered to the chosen option-1 value.
 * Nothing is preselected — the PDP deliberately preselects nothing after the
 * `value.selected` corruption bug (see CLAUDE.md), and the same reasoning
 * applies here: a wrong preselection ships a wrong size.
 */
(() => {
  if (window.__cardQuickAddOverlayInitialized) return;
  window.__cardQuickAddOverlayInitialized = true;

  const OVERLAY_SEL = '[data-card-quick-add]';
  const TRIGGER_SEL = '[data-card-quick-add-trigger]';

  // Parsed matrices and live selections, keyed by the overlay element itself so
  // a swapped-out card's state is garbage collected with its DOM.
  const matrices = new WeakMap();
  const selections = new WeakMap();

  const getMatrix = (overlay) => {
    let matrix = matrices.get(overlay);
    if (matrix !== undefined) return matrix;

    const script = overlay.querySelector('[data-card-quick-add-data]');
    try {
      matrix = JSON.parse(script.textContent);
      if (!matrix || !Array.isArray(matrix.o) || !Array.isArray(matrix.v)) matrix = null;
    } catch (error) {
      console.error('card-quick-add: could not parse variant data', error);
      matrix = null;
    }
    matrices.set(overlay, matrix);
    return matrix;
  };

  const getSelection = (overlay) => {
    let selection = selections.get(overlay);
    if (!selection) {
      selection = [];
      selections.set(overlay, selection);
    }
    return selection;
  };

  /*
   * A value at option `index` is offered when some available variant carries it
   * AND matches every selection made at a LOWER index. Higher indices are
   * ignored, which is what makes the cascade feel right for jeans: picking a
   * waist narrows the lengths, but picking a length never greys out waists.
   */
  const isValueAvailable = (matrix, index, value, selection) =>
    matrix.v.some((variant) => {
      const [, options, available] = variant;
      if (!available) return false;
      if (options[index] !== value) return false;
      for (let i = 0; i < index; i += 1) {
        if (selection[i] != null && options[i] !== selection[i]) return false;
      }
      return true;
    });

  const findVariant = (matrix, selection) => {
    if (selection.length !== matrix.o.length) return null;
    if (selection.some((value) => value == null)) return null;
    return (
      matrix.v.find((variant) => selection.every((value, i) => variant[1][i] === value)) || null
    );
  };

  const buildOptions = (overlay, matrix) => {
    const container = overlay.querySelector('[data-card-quick-add-options]');
    const fragment = document.createDocumentFragment();

    matrix.o.forEach((option, index) => {
      const group = document.createElement('div');
      group.className = 'card-quick-add__group';

      const legend = document.createElement('p');
      legend.className = 'card-quick-add__label';
      legend.id = `${overlay.dataset.cardQuickAddUid}-opt${index}`;
      legend.textContent = `${option.l}:`;
      group.appendChild(legend);

      const list = document.createElement('div');
      list.className = 'card-quick-add__values';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', legend.id);
      list.dataset.optionIndex = String(index);

      option.v.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'card-quick-add__value';
        button.dataset.optionIndex = String(index);
        button.dataset.optionValue = value;
        button.textContent = value;
        list.appendChild(button);
      });

      group.appendChild(list);
      fragment.appendChild(group);
    });

    container.replaceChildren(fragment);
  };

  const syncState = (overlay, matrix) => {
    const selection = getSelection(overlay);
    const form = overlay.querySelector('product-form');
    const submit = overlay.querySelector('.card-quick-add__submit');
    const input = overlay.querySelector('.product-variant-id');

    overlay.querySelectorAll('.card-quick-add__value').forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.optionValue;
      const available = isValueAvailable(matrix, index, value, selection);
      const selected = selection[index] === value;

      button.classList.toggle('is-selected', selected);
      button.classList.toggle('is-unavailable', !available);
      button.disabled = !available;
      button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      if (available) {
        button.removeAttribute('aria-describedby');
        button.removeAttribute('title');
      } else {
        // Disabled buttons are still announced; make the reason part of the name.
        button.title = (window.variantStrings?.unavailable_with_option || '[value] unavailable').replace(
          '[value]',
          value
        );
      }
    });

    const variant = findVariant(matrix, selection);
    const sellable = !!variant && variant[2] === 1;

    input.value = sellable ? String(variant[0]) : '';
    submit.disabled = !sellable;
    submit.classList.toggle('is-ready', sellable);

    // Clear any stale sold-out/error state left by a previous failed attempt.
    if (sellable) {
      const soldOut = submit.querySelector('.sold-out-message');
      const label = submit.querySelector('span');
      if (soldOut) soldOut.classList.add('hidden');
      if (label) label.classList.remove('hidden');
      submit.removeAttribute('aria-disabled');
      form?.querySelector('.product-form__error-message-wrapper')?.setAttribute('hidden', '');
    }
  };

  const closeOverlay = (overlay, returnFocus) => {
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    const card = overlay.closest('.card-wrapper');
    const trigger = card?.querySelector(TRIGGER_SEL);
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger.focus();
    }
    card?.classList.remove('card--quick-add-open');
  };

  const closeAll = (except) => {
    document.querySelectorAll(`${OVERLAY_SEL}:not([hidden])`).forEach((overlay) => {
      if (overlay !== except) closeOverlay(overlay, false);
    });
  };

  const openOverlay = (trigger) => {
    const card = trigger.closest('.card-wrapper');
    const overlay = card?.querySelector(OVERLAY_SEL);
    if (!overlay) return;

    const matrix = getMatrix(overlay);
    if (!matrix) return; // Malformed data: leave the card's normal link behaviour alone.

    closeAll(overlay);

    if (!overlay.dataset.cardQuickAddUid) {
      overlay.dataset.cardQuickAddUid = `cqa${Math.random().toString(36).slice(2, 9)}`;
      buildOptions(overlay, matrix);
    }

    syncState(overlay, matrix);
    overlay.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    card.classList.add('card--quick-add-open');

    const firstEnabled = overlay.querySelector('.card-quick-add__value:not([disabled])');
    if (firstEnabled) firstEnabled.focus({ preventScroll: true });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(TRIGGER_SEL);
    if (trigger) {
      event.preventDefault();
      // The trigger sits on top of the card's full-card link overlay.
      event.stopPropagation();
      const card = trigger.closest('.card-wrapper');
      const overlay = card?.querySelector(OVERLAY_SEL);
      if (overlay && !overlay.hidden) closeOverlay(overlay, true);
      else openOverlay(trigger);
      return;
    }

    const overlay = event.target.closest(OVERLAY_SEL);
    if (!overlay) {
      closeAll(null);
      return;
    }

    // Inside an open overlay: keep clicks off the card's link overlay beneath.
    event.stopPropagation();

    if (event.target.closest('[data-card-quick-add-close]')) {
      event.preventDefault();
      closeOverlay(overlay, true);
      return;
    }

    const value = event.target.closest('.card-quick-add__value');
    if (!value || value.disabled) return;

    event.preventDefault();
    const matrix = getMatrix(overlay);
    if (!matrix) return;

    const index = Number(value.dataset.optionIndex);
    const selection = getSelection(overlay);

    selection[index] = selection[index] === value.dataset.optionValue ? null : value.dataset.optionValue;

    // Changing an option can strand the choices that cascade from it.
    for (let i = index + 1; i < matrix.o.length; i += 1) {
      if (selection[i] != null && !isValueAvailable(matrix, i, selection[i], selection)) {
        selection[i] = null;
      }
    }

    syncState(overlay, matrix);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector(`${OVERLAY_SEL}:not([hidden])`);
    if (!open) return;
    event.preventDefault();
    closeOverlay(open, true);
  });

  // A successful add opens the cart drawer over the grid; drop the overlay so it
  // is not still sitting open behind it when the drawer closes.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      if (payload?.source !== 'product-form') return;
      closeAll(null);
    });
  }
})();
